import useCombinedPaginationResult, {PaginationQueryBuilder} from "../../Selection/Hooks/useCombinedPaginationResult";
import {useCallback} from "react";
import useTypes from "../../Type/Api/useTypes";
import ResourceSelection from "../../ResourcePlanner/Model/ResourceSelection";
import { Moment } from "moment";
import { Entity } from "../../../../../@Api/Model/Implementation/Entity";
import { EntityType } from "../../../../../@Api/Model/Implementation/EntityType";

interface Period {
    from: Moment,
    to: Moment
}

const CALENDAR_PAGE_SIZE = 100;

export default function usePaginatedEntityCalendar(
    resourceSelectionTypes: EntityType[],
    resourceSelections: ResourceSelection[],
    period: Period,
    employee: Entity
)
{
    const types = useTypes();
    const getEntityTypeCalendarQueryBuilder =
        useCallback(
            (entityType: EntityType, index: number): PaginationQueryBuilder =>
            {
                const selection = resourceSelections[index]
                const from = period?.from.toDate()
                const to = period?.to.toDate()
                if(!from || !to)
                {
                    return
                }
                return ({
                    type: entityType,
                    callback:
                        (builder: any) =>
                        {
                            builder
                                .join(selection.resourcePath)
                                .where(
                                    cb =>
                                        cb.relatedToEntity(
                                            selection.resourcePath,
                                            employee
                                        ))
                                .if(
                                    () =>
                                        selection.recurrence !== undefined,
                                    sb =>
                                        sb.where(
                                            cb =>
                                                cb.or(
                                                    ob =>
                                                        ob
                                                            .overlapsWith(
                                                                selection.startDateFieldPath,
                                                                selection.endDateFieldPath,
                                                                from,
                                                                to
                                                            )
                                                            .overlapsWithNullableEnd(
                                                                selection.recurrence.startDateFieldPath,
                                                                selection.recurrence.endDateFieldPath,
                                                                from,
                                                                to
                                                            )
                                                )
                                        )
                                )
                                .if(
                                    () =>
                                        selection.recurrence === undefined,
                                    sb =>
                                        sb.where(
                                            cb =>
                                                cb.overlapsWith(
                                                    selection.startDateFieldPath,
                                                    selection.endDateFieldPath,
                                                    from,
                                                    to
                                                )
                                        )
                                )
                                .if(
                                    () =>
                                        selection.filter !== undefined,
                                    sb =>
                                        sb.where(
                                            cb =>
                                                cb.filter(
                                                    selection.filter,
                                                    {
                                                        parameter: selection.parameter,
                                                    }
                                                )
                                        )
                                )
                                .if(
                                    () => true,
                                    sb =>
                                        selection.entityType
                                            .bespoke
                                            .getListDependencies()
                                            .forEach(
                                                dependencyPath =>
                                                    sb.join(dependencyPath)
                                            )
                                )
                        },
                })
            },
            [
                types,
                resourceSelections,
                employee,
                period
            ]
        );

    const {
        results,
        loadMore,
        hasMore,
        isLoading
    } =
        useCombinedPaginationResult(
            resourceSelectionTypes
                .map(
                    (e, idx) => getEntityTypeCalendarQueryBuilder(e, idx)
                ),
            [
                getEntityTypeCalendarQueryBuilder,
                resourceSelectionTypes,
                period,
                employee,
                resourceSelections
            ]
        )

    return {
        results,
        loadMore,
        hasMore,
        isLoading
    }
}
